import React, { useCallback, useEffect, useMemo } from "react";
import { UserOrganisationType } from "@/api/query/useOrganisationDetailsQuery";
import {
  useEffortCalculation,
  calculateTaskEffort,
  calculateTotalHours,
} from "@/hooks/useEffortCalculation";
import InputNumber from "@/components/common/InputNumber";
import UserAvatar from "../ui/userAvatar";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { Task } from "@/api/mutation/useTaskCreateMutation";
import { Check, X, AlertTriangle } from "lucide-react";
import { countLeaveDaysInRange } from "@/helperFuntions/checkUserLeave";

type AssignedUser = {
  taskAssignUsersId: string;
  assignedByUserId: string;
  assignedToUserId: string;
  workingHours: number;
  taskEffort: number;
};

type Props = {
  task: Task;
  member: UserOrganisationType;
  assignedInfo: AssignedUser | undefined;
  taskDuration: number;
  isAssigned: boolean;
  onToggleAssign: (member: UserOrganisationType, workingHours: number) => void;
  onUpdateAssign: (
    member: UserOrganisationType,
    workingHours: number,
    taskEffort: number
  ) => void;
};

const workingHoursPerDay = 8;

const MemberAssignmentItem = ({
  task,
  member,
  assignedInfo,
  taskDuration,
  isAssigned,
  onToggleAssign,
  onUpdateAssign,
}: Props) => {
  const { t } = useTranslation();
  const taskStart = useMemo(() => {
    const d = new Date(task?.startDate);
    return isNaN(d.getTime()) ? new Date() : d;
  }, [task?.startDate]);

  const taskEnd = useMemo(() => {
    if (task?.endDate) {
      const d = new Date(task.endDate);
      if (!isNaN(d.getTime())) return d;
    }
    // Fallback: estimate taskEnd by adding working days or calendar duration
    const end = new Date(taskStart);
    let remaining = Math.max(1, taskDuration || 1);
    while (remaining > 1) {
      end.setDate(end.getDate() + 1);
      const day = end.getDay();
      if (day !== 0 && day !== 6) {
        remaining--;
      }
    }
    return end;
  }, [task?.endDate, taskStart, taskDuration]);

  const leaveDays = useMemo(() => {
    return countLeaveDaysInRange(
      member.user.userAvailability ?? [],
      taskStart,
      taskEnd
    );
  }, [member.user.userAvailability, taskStart, taskEnd]);

  const effectiveDuration = Math.max(1, Number(taskDuration) || 1);
  const adjustedDuration = Math.max(0, effectiveDuration - leaveDays);
  const isFullyUnavailable = adjustedDuration <= 0;

  const initialEffort = useMemo(() => {
    if (assignedInfo) {
      return calculateTaskEffort(
        assignedInfo.workingHours,
        adjustedDuration,
        workingHoursPerDay
      );
    }
    return 100;
  }, [assignedInfo, adjustedDuration]);

  const { taskEffort, totalHours, setTaskEffort, setTotalHours } =
    useEffortCalculation(
      initialEffort ?? 100,
      adjustedDuration,
      assignedInfo?.workingHours ?? 8
    );

  useEffect(() => {
    const newEffort = taskEffort ?? 100;
    const newHours = calculateTotalHours(
      newEffort,
      adjustedDuration,
      workingHoursPerDay
    );
    setTotalHours(assignedInfo?.workingHours ?? newHours);
  }, [assignedInfo?.workingHours, adjustedDuration]);

  const debouncedUpdate = useCallback(
    debounce((newHours: number, newEffort: number) => {
      onUpdateAssign(member, newHours, newEffort);
    }, 800),
    [member, onUpdateAssign]
  );

  const handleEffortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawEffort = Math.min(Math.max(0, Number(e.target.value)), 100);
    const newEffort = Math.round(rawEffort * 100) / 100;
    const newHours = calculateTotalHours(
      newEffort,
      adjustedDuration,
      workingHoursPerDay
    );
    setTaskEffort(newEffort);
    setTotalHours(newHours);
    debouncedUpdate(newHours, newEffort);
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawHours = Math.max(0, Number(e.target.value));
    const inputHours = Math.round(rawHours * 100) / 100;
    const calculatedEffort = calculateTaskEffort(
      inputHours,
      adjustedDuration,
      workingHoursPerDay
    );

    if (calculatedEffort > 100) {
      const clampedHours = calculateTotalHours(
        100,
        adjustedDuration,
        workingHoursPerDay
      );
      setTotalHours(clampedHours);
      setTaskEffort(100);
      debouncedUpdate(clampedHours, 100);
    } else {
      setTotalHours(inputHours);
      setTaskEffort(calculatedEffort);
      debouncedUpdate(inputHours, calculatedEffort);
    }
  };

  const fullName = `${member.user.firstName || ""} ${member.user.lastName || ""}`.trim() || member.user.email;

  return (
    <div
      className={`group flex flex-col p-2 rounded-lg border transition-all ${isAssigned && member.user.isActive
        ? "bg-primary-50/40 border-primary-200/80 shadow-2xs"
        : "bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50/80"
        } ${!member.user.isActive ? "opacity-50 cursor-not-allowed bg-slate-50" : ""
        }`}
    >
      {/* Top Header: Avatar + User Info + Assign Toggle Button */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="relative flex-shrink-0">
            <UserAvatar user={member.user} className="w-7 h-7 rounded-full ring-1 ring-white shadow-2xs" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-slate-800 truncate leading-tight transition-colors">
              {fullName}
            </span>
            <span className="text-[10px] text-slate-400 truncate leading-tight">
              {member.user.email}
            </span>
          </div>
        </div>

        {/* Action Toggle Button */}
        <div>
          {isAssigned ? (
            <button
              type="button"
              disabled={!member.user.isActive}
              onClick={() => {
                if (!member.user.isActive) return;
                onToggleAssign(member, totalHours);
              }}
              title={t("remove")}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors border border-rose-200/60"
            >
              <X className="w-3 h-3" />
              <span>{t("remove")}</span>
            </button>
          ) : (
            <button
              type="button"
              disabled={!member.user.isActive}
              onClick={() => {
                if (!member.user.isActive) return;
                onToggleAssign(member, totalHours);
              }}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-slate-100 text-slate-700 hover:bg-primary-50 hover:text-primary-700 transition-colors border border-slate-200"
            >
              <Check className="w-3 h-3 opacity-60" />
              <span>{t("assign")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Expanded Effort & Hours Inputs when Assigned */}
      {isAssigned && !task.subtasks?.length && (
        <div className="mt-1.5 pt-1.5 border-t border-slate-200/60 grid grid-cols-3 gap-1.5 bg-slate-50/80 p-1.5 rounded-md">
          {/* Effort (%) */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
              {t("Effort (%)")}
            </span>
            <InputNumber
              min={0}
              max={100}
              value={taskEffort <= 0 ? 0 : taskEffort}
              disabled={isFullyUnavailable || !member.user.isActive}
              className="w-full h-6 px-1.5 text-xs font-semibold rounded border-slate-200 focus:border-primary-500 bg-white"
              onChange={handleEffortChange}
            />
          </div>

          {/* Duration (days) */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
              {t("Days")}
            </span>
            <InputNumber
              min={1}
              value={adjustedDuration <= 0 ? 0 : adjustedDuration}
              disabled
              className="w-full h-6 px-1.5 text-xs font-semibold rounded border-slate-200 bg-slate-100/80 text-slate-500"
            />
          </div>

          {/* Total Hours */}
          <div className="flex flex-col gap-0.5">
            <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">
              {t("Hours")}
            </span>
            <InputNumber
              min={0}
              value={totalHours <= 0 ? 0 : totalHours}
              disabled={isFullyUnavailable || !member.user.isActive}
              className="w-full h-6 px-1.5 text-xs font-semibold rounded border-slate-200 focus:border-primary-500 bg-white"
              onChange={handleHoursChange}
            />
          </div>
        </div>
      )}

      {/* Leave warning indicator */}
      {leaveDays > 0 && (
        <div className="mt-1 flex items-center gap-1.5 text-[10px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">
          <AlertTriangle className="w-3 h-3 flex-shrink-0 text-amber-500" />
          <span>{t("availabilityMessage", { leaveDays })}</span>
        </div>
      )}
    </div>
  );
};

export default MemberAssignmentItem;
